import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Block as BlockIcon,
  ContentCopy as ContentCopyIcon,
  DeleteForever as DeleteForeverIcon,
  EditOutlined as EditIcon,
  LockOpen as ReactivateIcon,
  Sync as RepairIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useAdminUsers } from '../hooks/useAdminUsers';
import {
  ADMIN_MODULE_LABELS,
  ADMIN_MODULE_ORDER,
  AdminAccessError,
  AdminAction,
  AdminPermissions,
  AdminUserSummary,
  CreateAdminUserConflict,
  extractReuseUidConflict,
} from '../services/firebaseFunctions';

type DialogMode = 'create' | 'edit';

interface AdminFormState {
  mode: DialogMode;
  uid?: string;
  email: string;
  password: string;
  displayName: string;
  permissions: AdminPermissions;
}

interface PendingReuseConfirmation {
  conflict: CreateAdminUserConflict;
  form: AdminFormState;
}

interface PendingSimpleConfirmation {
  actionType: 'disable' | 'delete';
  admin: AdminUserSummary;
}

// Novo administrador nasce com a matriz inteira em "editar" (UX: o operador remove o que
// não é necessário, em vez de conceder módulo a módulo) — precisa estar no ESTADO inicial,
// não só no default visual do editor, senão módulo não tocado nunca entra em `form.permissions`
// (o `onChange` do Select só dispara quando o valor muda) e a permissão "exibida" não é salva.
const fullPermissions = (): AdminPermissions =>
  ADMIN_MODULE_ORDER.reduce((acc, moduleId) => {
    acc[moduleId] = 'editar';
    return acc;
  }, {} as AdminPermissions);

const emptyForm = (): AdminFormState => ({
  mode: 'create',
  email: '',
  password: generatePassword(),
  displayName: '',
  permissions: fullPermissions(),
});

// Alfabeto sem caracteres visualmente ambíguos (0/O, 1/l/I) — mesma preocupação de
// `reedy-fiscal/src/pages/UsuariosPage.tsx`, implementação própria (referência de padrão a
// avaliar, não a copiar — `specs/064-controle-acesso-admin/tasks.md` §"Handoff").
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const LOWER = 'abcdefghijkmnopqrstuvwxyz';
const DIGITS = '23456789';

function randomIndex(max: number): number {
  // `crypto.getRandomValues` é a via preferida (mesma preocupação de segurança de
  // `reedy-fiscal/src/pages/UsuariosPage.tsx`); jsdom (ambiente de teste) não expõe `crypto`
  // globalmente, então cai em `Math.random()` só nesse cenário — nunca em produção/browser real.
  const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoObj?.getRandomValues) {
    const buffer = new Uint32Array(1);
    cryptoObj.getRandomValues(buffer);
    return buffer[0] % max;
  }
  return Math.floor(Math.random() * max);
}

function generatePassword(length = 10): string {
  const all = `${UPPER}${LOWER}${DIGITS}`;
  const chars = [
    UPPER[randomIndex(UPPER.length)],
    LOWER[randomIndex(LOWER.length)],
    DIGITS[randomIndex(DIGITS.length)],
  ];
  while (chars.length < length) chars.push(all[randomIndex(all.length)]);
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function formatDateTime(value?: string): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString('pt-BR');
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Não foi possível concluir a operação.';
}

/** Editor da matriz módulo × ação — nível ordenado ('editar' ⊃ 'ver'), não dois booleanos. */
const PermissionsMatrixEditor: React.FC<{
  permissions: AdminPermissions;
  onChange: (permissions: AdminPermissions) => void;
  disabled?: boolean;
}> = ({ permissions, onChange, disabled }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Módulo</TableCell>
        <TableCell>Permissão</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {ADMIN_MODULE_ORDER.map((moduleId) => {
        // Exibe o estado REAL de `permissions` — nunca um default otimista aqui. O default de
        // "editar" pra admin novo é responsabilidade de quem inicializa o form (`emptyForm`),
        // não deste editor: mudar o fallback aqui faria a matriz mentir sobre permissão de um
        // administrador já existente ao editar (mostraria "Editar" pra módulo sem acesso real).
        const value = permissions[moduleId] ?? 'none';
        return (
          <TableRow key={moduleId}>
            <TableCell>{ADMIN_MODULE_LABELS[moduleId]}</TableCell>
            <TableCell>
              <Select
                size="small"
                fullWidth
                value={value}
                disabled={disabled}
                inputProps={{ 'aria-label': `Permissão em ${ADMIN_MODULE_LABELS[moduleId]}` }}
                onChange={(event) => {
                  const next = event.target.value as AdminAction | 'none';
                  const updated = { ...permissions };
                  if (next === 'none') delete updated[moduleId];
                  else updated[moduleId] = next;
                  onChange(updated);
                }}
              >
                <MenuItem value="none">Sem acesso</MenuItem>
                <MenuItem value="ver">Ver</MenuItem>
                <MenuItem value="editar">Editar</MenuItem>
              </Select>
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
);

const PermissionsSummary: React.FC<{ permissions: AdminPermissions }> = ({ permissions }) => {
  const entries = ADMIN_MODULE_ORDER.filter((moduleId) => permissions[moduleId]);
  if (entries.length === 0) return <Typography variant="caption" color="text.secondary">Nenhuma permissão</Typography>;
  return (
    <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap', maxWidth: 320 }}>
      {entries.map((moduleId) => (
        <Chip
          key={moduleId}
          size="small"
          label={`${ADMIN_MODULE_LABELS[moduleId]}: ${permissions[moduleId] === 'editar' ? 'Editar' : 'Ver'}`}
          color={permissions[moduleId] === 'editar' ? 'primary' : 'default'}
          variant="outlined"
        />
      ))}
    </Stack>
  );
};

/**
 * Página "Administradores" (REEDY-17) — CRUD de administradores individuais com matriz de
 * permissões módulo × ação. Substitui o conceito de conta compartilhada única.
 *
 * Cobre, nesta única página, as jornadas J1 (gestor completo) e a metade client-side de J5
 * (a listagem nunca cacheia — cada `refresh()` reflete o estado vigente do backend), além de
 * J6 ("Remover acesso") e J7 ("Exclusão de conta") — duas ações com alcances diferentes e uma
 * confirmação cada: "Remover acesso" derruba só o acesso ao painel e preserva a conta de login;
 * "Exclusão de conta" apaga a conta Firebase Auth, de forma irreversível.
 */
const AdminsPage: React.FC = () => {
  const { firebaseUser, hasPermission } = useAuth();
  const { admins, loading, error, createAdmin, updateAdmin, setAdminStatus, deleteAdmin } = useAdminUsers();
  const canManage = hasPermission('administradores', 'editar');
  const selfUid = firebaseUser?.uid;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<AdminFormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [localError, setLocalError] = useState('');

  const [reuseConfirmation, setReuseConfirmation] = useState<PendingReuseConfirmation | null>(null);
  const [simpleConfirmation, setSimpleConfirmation] = useState<PendingSimpleConfirmation | null>(null);
  const [deleteEmailConfirm, setDeleteEmailConfirm] = useState('');

  const formError = useMemo(() => {
    if (form.mode === 'create' && !isValidEmail(form.email)) return 'Informe um e-mail válido.';
    if (form.mode === 'create' && form.password.length < 6) return 'Senha deve ter pelo menos 6 caracteres.';
    return '';
  }, [form]);

  const openCreate = () => {
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (admin: AdminUserSummary) => {
    setForm({
      mode: 'edit',
      uid: admin.uid,
      email: admin.email,
      password: '',
      displayName: admin.displayName ?? '',
      permissions: { ...admin.permissions },
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setLocalError('');
  };

  const save = async () => {
    if (formError) {
      setLocalError(formError);
      return;
    }
    setSaving(true);
    setLocalError('');
    try {
      if (form.mode === 'create') {
        const result = await createAdmin({
          email: form.email.trim(),
          password: form.password,
          displayName: form.displayName.trim() || undefined,
          permissions: form.permissions,
        });
        setDialogOpen(false);
        setFeedback(
          `Administrador ${result.admin.email} criado. Senha temporária: ${form.password} — ` +
            'entregue-a ao novo administrador; ela não é exibida novamente e não foi enviada por e-mail.'
        );
      } else {
        await updateAdmin({ uid: form.uid!, permissions: form.permissions, displayName: form.displayName.trim() || undefined });
        setDialogOpen(false);
        setFeedback('Permissões atualizadas.');
      }
    } catch (caught) {
      const err = caught as AdminAccessError;
      const conflict = form.mode === 'create' ? extractReuseUidConflict(err) : null;
      if (conflict) {
        setReuseConfirmation({ conflict, form });
      } else {
        setLocalError(errorMessage(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const cancelReuseConfirmation = () => {
    if (saving) return;
    setReuseConfirmation(null);
  };

  const confirmReuse = async () => {
    if (!reuseConfirmation) return;
    setSaving(true);
    setLocalError('');
    try {
      const result = await createAdmin({
        email: reuseConfirmation.form.email.trim(),
        displayName: reuseConfirmation.form.displayName.trim() || undefined,
        permissions: reuseConfirmation.form.permissions,
        confirmReuseUid: reuseConfirmation.conflict.uid,
      });
      setReuseConfirmation(null);
      setDialogOpen(false);
      setFeedback(
        `Administrador ${result.admin.email} criado reaproveitando uma conta já existente no ` +
          'Firebase. A senha atual dessa conta foi mantida — este painel não a alterou.'
      );
    } catch (caught) {
      setReuseConfirmation(null);
      setLocalError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  const askDisable = (admin: AdminUserSummary) => setSimpleConfirmation({ actionType: 'disable', admin });
  const askDelete = (admin: AdminUserSummary) => {
    setDeleteEmailConfirm('');
    setSimpleConfirmation({ actionType: 'delete', admin });
  };

  // Confirmação única para as duas ações destrutivas — nenhum passo extra antes de aplicar. Falha é
  // sempre terminal e vai para o alerta da página, inclusive o `internal` de "acesso removido mas
  // conta de login não pôde ser excluída", cuja mensagem já vem pronta do backend.
  const runDestructiveAction = async (actionType: 'disable' | 'delete', admin: AdminUserSummary) => {
    setSaving(true);
    setLocalError('');
    try {
      if (actionType === 'disable') {
        await setAdminStatus({ uid: admin.uid, status: 'disabled' });
        setFeedback(`Acesso ao Reedy Admin removido de ${admin.email}. A conta de login continua existindo.`);
      } else {
        await deleteAdmin(admin.uid);
        setFeedback(`Conta de ${admin.email} excluída. A credencial não existe mais em nenhum produto Reedy.`);
      }
      setSimpleConfirmation(null);
    } catch (caught) {
      setSimpleConfirmation(null);
      setLocalError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  const reactivate = async (admin: AdminUserSummary) => {
    setSaving(true);
    setLocalError('');
    try {
      await setAdminStatus({ uid: admin.uid, status: 'active' });
      setFeedback(`Administrador ${admin.email} reativado.`);
    } catch (caught) {
      setLocalError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  const repairClaim = async (admin: AdminUserSummary) => {
    setSaving(true);
    setLocalError('');
    try {
      await updateAdmin({ uid: admin.uid, permissions: admin.permissions, displayName: admin.displayName });
      setFeedback(`Sincronização de ${admin.email} reaplicada.`);
    } catch (caught) {
      setLocalError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  };

  const deleteEmail = simpleConfirmation?.admin.email.trim().toLowerCase() ?? '';
  const deleteConfirmed = simpleConfirmation?.actionType !== 'delete'
    || (Boolean(deleteEmail) && deleteEmailConfirm.trim().toLowerCase() === deleteEmail);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} sx={{ alignItems: { xs: 'stretch', md: 'center' }, gap: 2, mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }} gutterBottom>Administradores</Typography>
          <Typography color="text.secondary">
            Contas individuais de acesso ao Reedy Admin e suas permissões por módulo.
          </Typography>
        </Box>
        <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Administradores.'}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={!canManage}>
              Novo administrador
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {(error || localError || feedback) && (
        <Alert
          severity={error || localError ? 'error' : 'success'}
          sx={{ mb: 2 }}
          onClose={() => setFeedback('')}
        >
          {error || localError || feedback}
        </Alert>
      )}

      <TableContainer component={Box}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Administrador</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Permissões</TableCell>
              <TableCell>Último acesso</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.map((admin) => {
              const isSelf = admin.uid === selfUid;
              return (
                <TableRow key={admin.uid}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{admin.email}</Typography>
                    {admin.displayName && (
                      <Typography variant="caption" color="text.secondary">{admin.displayName}</Typography>
                    )}
                    {isSelf && <Chip label="Você" size="small" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={admin.status === 'disabled' ? 'Desativado' : 'Ativo'}
                        color={admin.status === 'disabled' ? 'warning' : 'success'}
                        variant="outlined"
                        size="small"
                      />
                      {!admin.claimInSync && (
                        <Tooltip title="Documento e credencial fora de sincronia. Use 'Reparar' para reaplicar.">
                          <Chip
                            icon={<WarningIcon />}
                            label="Fora de sincronia"
                            color="error"
                            variant="outlined"
                            size="small"
                          />
                        </Tooltip>
                      )}
                      {admin.authDisabled && <Chip label="Auth desativado" color="warning" size="small" variant="outlined" />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <PermissionsSummary permissions={admin.permissions} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDateTime(admin.lastSignInAt)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={isSelf ? 'Você não pode alterar suas próprias permissões' : 'Editar permissões'}>
                      <span>
                        <IconButton
                          aria-label={isSelf ? 'Você não pode alterar suas próprias permissões' : 'Editar permissões'}
                          size="small"
                          onClick={() => openEdit(admin)}
                          disabled={!canManage || isSelf}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {!admin.claimInSync && (
                      <Tooltip title="Reparar sincronização (reaplica a credencial)">
                        <span>
                          <IconButton
                            aria-label="Reparar sincronização"
                            size="small"
                            onClick={() => void repairClaim(admin)}
                            disabled={!canManage || saving}
                          >
                            <RepairIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {admin.status === 'disabled' ? (
                      <Tooltip title="Reativar">
                        <span>
                          <IconButton aria-label="Reativar" size="small" color="success" onClick={() => void reactivate(admin)} disabled={!canManage || saving}>
                            <ReactivateIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={isSelf ? 'Você não pode remover o próprio acesso' : 'Remover acesso'}>
                        <span>
                          <IconButton
                            aria-label={isSelf ? 'Você não pode remover o próprio acesso' : 'Remover acesso'}
                            size="small"
                            color="warning"
                            onClick={() => askDisable(admin)}
                            disabled={!canManage || isSelf || saving}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title={isSelf ? 'Você não pode excluir a própria conta' : 'Exclusão de conta'}>
                      <span>
                        <IconButton
                          aria-label={isSelf ? 'Você não pode excluir a própria conta' : 'Exclusão de conta'}
                          size="small"
                          color="error"
                          onClick={() => askDelete(admin)}
                          disabled={!canManage || isSelf || saving}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {admins.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Alert severity="info" sx={{ m: 2 }}>Nenhum administrador cadastrado.</Alert>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de criação/edição — matriz de permissões módulo × ação */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{form.mode === 'create' ? 'Novo administrador' : 'Editar administrador'}</DialogTitle>
        <DialogContent>
          <Stack sx={{ gap: 2, mt: 1 }}>
            <TextField
              label="E-mail"
              type="email"
              value={form.email}
              onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
              disabled={form.mode === 'edit'}
              required
              fullWidth
            />
            <TextField
              label="Nome de exibição (opcional)"
              value={form.displayName}
              onChange={(event) => setForm((prev) => ({ ...prev, displayName: event.target.value }))}
              fullWidth
            />
            {form.mode === 'create' && (
              <Stack sx={{ gap: 1 }}>
                <Alert severity="info" variant="outlined">
                  Defina a senha temporária agora e entregue-a você mesmo ao novo administrador —
                  não há envio de e-mail de convite.
                </Alert>
                <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1 }}>
                  <TextField
                    label="Senha temporária"
                    value={form.password}
                    onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                    required
                    fullWidth
                  />
                  <Button variant="outlined" onClick={() => setForm((prev) => ({ ...prev, password: generatePassword() }))}>
                    Gerar
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(form.password);
                        setFeedback('Senha copiada para a área de transferência.');
                      } catch {
                        setLocalError('Não foi possível copiar a senha automaticamente.');
                      }
                    }}
                    disabled={!form.password}
                  >
                    Copiar
                  </Button>
                </Stack>
              </Stack>
            )}
            <Typography variant="subtitle2">Permissões por módulo</Typography>
            <Box sx={{ maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <PermissionsMatrixEditor
                permissions={form.permissions}
                onChange={(permissions) => setForm((prev) => ({ ...prev, permissions }))}
              />
            </Box>
            {(formError || localError) && <Alert severity="warning">{formError || localError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={() => void save()} disabled={saving || Boolean(formError)}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação em duas etapas (a) — e-mail já existente no Firebase Auth (R17/R22) */}
      <Dialog open={Boolean(reuseConfirmation)} onClose={cancelReuseConfirmation} maxWidth="sm" fullWidth>
        <DialogTitle>Este e-mail já tem uma conta no Firebase</DialogTitle>
        <DialogContent>
          <Stack sx={{ gap: 2 }}>
            <Alert severity="warning">
              <AlertTitle>A senha informada NÃO será aplicada</AlertTitle>
              Já existe uma conta com o e-mail <strong>{reuseConfirmation?.form.email}</strong> no
              Firebase. Confirmando, o administrador é criado reaproveitando essa conta — a senha
              e o e-mail atuais dela são mantidos como estão; este painel não os altera.
            </Alert>
            <Stack sx={{ gap: 0.5 }}>
              <Typography variant="body2">UID: {reuseConfirmation?.conflict.uid}</Typography>
              <Typography variant="body2">Criada em: {formatDateTime(reuseConfirmation?.conflict.creationTime)}</Typography>
              <Typography variant="body2">Último login: {formatDateTime(reuseConfirmation?.conflict.lastSignInTime)}</Typography>
              <Typography variant="body2">
                Provedores: {reuseConfirmation?.conflict.providerIds?.join(', ') || '—'}
              </Typography>
            </Stack>
            <Alert severity="info" variant="outlined">
              Isto é um indício de que a conta pertence a outra pessoa/uso, não uma prova — confirme
              apenas se tiver certeza de que é a pessoa correta.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelReuseConfirmation} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={() => void confirmReuse()} disabled={saving}>
            {saving ? 'Confirmando...' : 'Confirmar reaproveitamento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/*
        Confirmação única das duas ações destrutivas — "Remover acesso" e "Exclusão de conta".
        Não existe passo extra nem texto condicional por tipo de conta: o painel não sabe onde a
        credencial é usada hoje, e verificar isso antes de confirmar é responsabilidade do
        operador. "Exclusão de conta" mantém a confirmação por e-mail digitado.
      */}
      <Dialog open={Boolean(simpleConfirmation)} onClose={() => setSimpleConfirmation(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={simpleConfirmation?.actionType === 'delete' ? { color: 'error.main', fontWeight: 700 } : undefined}>
          {simpleConfirmation?.actionType === 'delete' ? 'Exclusão de conta' : 'Remover acesso'}
        </DialogTitle>
        <DialogContent>
          <Stack sx={{ gap: 2 }}>
            {simpleConfirmation?.actionType === 'delete' ? (
              <>
                <Alert severity="error">
                  <AlertTitle>Ação irreversível: a conta de login deixa de existir</AlertTitle>
                  Esta ação exclui a conta Firebase Auth de{' '}
                  <strong>{simpleConfirmation?.admin.email}</strong> — não apenas o acesso ao
                  painel. A credencial deixa de existir em <strong>todos os produtos Reedy</strong>{' '}
                  (Reedy Admin, aplicativo e Reedy Fiscal) e não há como desfazer.
                </Alert>
                <Alert severity="error" variant="outlined">
                  Antes de confirmar, verifique você mesmo se esta conta é usada em outras áreas do
                  sistema: o painel não faz essa verificação e exclui a conta em qualquer caso. Se a
                  intenção for apenas tirar o acesso ao Reedy Admin, cancele e use “Remover acesso”.
                </Alert>
                <TextField
                  label="Digite o e-mail para confirmar"
                  value={deleteEmailConfirm}
                  onChange={(event) => setDeleteEmailConfirm(event.target.value)}
                  helperText={`Confirme digitando: ${simpleConfirmation?.admin.email ?? ''}`}
                  error={Boolean(deleteEmailConfirm) && !deleteConfirmed}
                  fullWidth
                />
              </>
            ) : (
              <Alert severity="warning">
                <AlertTitle>A conta de login continua existindo</AlertTitle>
                {simpleConfirmation?.admin.email} perde o acesso ao Reedy Admin até ser
                reativado(a). A conta não é bloqueada: a pessoa continua usando os outros produtos
                Reedy com a mesma credencial.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimpleConfirmation(null)} disabled={saving}>Cancelar</Button>
          <Button
            color={simpleConfirmation?.actionType === 'delete' ? 'error' : 'warning'}
            variant="contained"
            disabled={saving || !deleteConfirmed}
            onClick={() => simpleConfirmation && void runDestructiveAction(simpleConfirmation.actionType, simpleConfirmation.admin)}
          >
            {saving
              ? 'Aplicando...'
              : simpleConfirmation?.actionType === 'delete'
                ? 'Excluir conta permanentemente'
                : 'Remover acesso'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminsPage;
